import { gsap } from 'gsap'

/**
 * Fade réutilisable via attributs HTML :
 *   animation-fade="up|down|left|right|in"
 *   animation-delay="0.2"   (secondes, optionnel)
 *
 * Sur un parent : animation-fade-group
 * → les enfants [animation-fade] se déclenchent quand le parent entre.
 *
 * Important : pas de translate initial (y/x) avant le play.
 * Sinon en bas de page l’élément est poussé hors viewport → jamais de trigger.
 */
const OFFSETS = {
  up: { y: 36, x: 0 },
  down: { y: -36, x: 0 },
  left: { y: 0, x: 36 },
  right: { y: 0, x: -36 },
  in: { y: 0, x: 0 },
}

export class Fade {
  constructor () {
    this._elements = [...document.querySelectorAll('[animation-fade]')]

    if (!this._elements.length) {
      return
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return
    }

    this.init()
  }

  init () {
    /** @type {Map<Element, HTMLElement[]>} */
    const byTrigger = new Map()

    this._elements.forEach((el) => {
      const trigger = el.closest('[animation-fade-group]') || el
      if (!byTrigger.has(trigger)) {
        byTrigger.set(trigger, [])
      }
      byTrigger.get(trigger).push(el)

      // ponytail: opacity seule — le translate est appliqué au play (évite hors-viewport)
      gsap.set(el, { autoAlpha: 0 })
    })

    const playGroup = (els) => {
      els.forEach((el) => {
        if (el.dataset.fadePlayed) return
        el.dataset.fadePlayed = '1'

        const direction = el.getAttribute('animation-fade') || 'up'
        const delay = parseFloat(el.getAttribute('animation-delay') || '0') || 0
        const offset = OFFSETS[direction] || OFFSETS.up

        gsap.fromTo(
          el,
          { autoAlpha: 0, x: offset.x, y: offset.y },
          {
            autoAlpha: 1,
            x: 0,
            y: 0,
            duration: 0.75,
            delay,
            ease: 'power2.out',
            overwrite: 'auto',
          },
        )
      })
    }

    if (!('IntersectionObserver' in window)) {
      byTrigger.forEach((els) => playGroup(els))
      return
    }

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        playGroup(byTrigger.get(entry.target) || [])
        io.unobserve(entry.target)
      })
    }, {
      // 1 px suffit — avec un seuil > 0 les derniers blocs (peu visibles) restaient coincés
      threshold: 0,
      rootMargin: '0px',
    })

    byTrigger.forEach((_, trigger) => io.observe(trigger))

    // Filet : fin de scroll (Lenis inclus via scroll natif / scrollTo)
    const flush = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight
      if (max > 0 && window.scrollY < max - 80) return

      byTrigger.forEach((els, trigger) => {
        const rect = trigger.getBoundingClientRect()
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          playGroup(els)
          io.unobserve(trigger)
        }
      })
    }

    let settle
    const onScroll = () => {
      flush()
      clearTimeout(settle)
      settle = setTimeout(flush, 150)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', flush, { passive: true })
  }
}
